import React, { useMemo, useContext } from 'react';
import { t } from '@sm/intl';
import { CountryCodes } from '@sm/locale/dist/localeSets';
import { StaticContext } from '@sm/webassets';

import { Packages, SkuTypes } from '~shared/constants/pricing';
import { MONTHLY_PACKAGES, TEAMS_PACKAGES } from '~pricing/lib/constants';
import {
  formatCostInLocaleCurrency,
  formatCostInSkuCurrency,
  formatMonthlyCostInLocaleCurrency,
  getCoreCostReplicaForBasicPackage,
  getSkuCostByType,
} from '~pricing/lib/lib-helpers';

import { PackageSkuCosts } from '~shared/types/pricing/package';
import { PriceDisplayHook } from '../types';

import COPY from './copy';
import { usePackages } from '~shared/hooks/usePackages';

const MINIMUM_EXTRA_TEAM_SEATS = 2;

export default function usePriceDisplay(packageName: Packages, skuCosts: PackageSkuCosts): PriceDisplayHook {
  const {
    environment: { countryCode },
  } = useContext(StaticContext);
  const { displayPackages } = usePackages();
  const isBasic = useMemo(() => packageName === Packages.Basic, [packageName]);
  const isBrazil = useMemo(() => countryCode === CountryCodes.BR, [countryCode]);
  const isMonthlyPackage = useMemo(() => MONTHLY_PACKAGES.includes(packageName), [packageName]);
  const isTeamsPackage = useMemo(() => TEAMS_PACKAGES.includes(packageName), [packageName]);

  const [coreSeatCost, additionalSeatCost] = useMemo(() => {
    // fake a SKU for basic
    const coreSeatSku = isBasic
      ? getCoreCostReplicaForBasicPackage(displayPackages)
      : getSkuCostByType(skuCosts, SkuTypes.CoreSeat);
    const additionalSeatSku = isBasic ? coreSeatSku : getSkuCostByType(skuCosts, SkuTypes.AdditionalSeat);
    return [coreSeatSku, additionalSeatSku];
  }, [isBasic, skuCosts, displayPackages]);

  const monthlyFormattedCost = useMemo(
    () =>
      coreSeatCost &&
      (isMonthlyPackage ? formatCostInLocaleCurrency(coreSeatCost) : formatMonthlyCostInLocaleCurrency(coreSeatCost)),
    [isMonthlyPackage, coreSeatCost]
  );

  const annualFormattedCost = useMemo(
    () =>
      coreSeatCost &&
      (isMonthlyPackage
        ? formatCostInSkuCurrency((coreSeatCost.cost / 100) * 12, coreSeatCost)
        : formatCostInLocaleCurrency(coreSeatCost)),
    [isMonthlyPackage, coreSeatCost]
  );

  const annualTeamsFormattedCost = useMemo(() => {
    let retVal: React.ReactElement | string = '';

    if (isTeamsPackage && coreSeatCost && additionalSeatCost) {
      // a base teams cost will be one core seat + two additional seats
      // I don't know if these two seat types have ever had different values,
      // but some day they will... some day...
      const baseCost = Number(coreSeatCost.cost) + Number(additionalSeatCost.cost) * MINIMUM_EXTRA_TEAM_SEATS;
      retVal = formatCostInSkuCurrency(baseCost / 100, coreSeatCost);
    }

    return retVal;
  }, [isTeamsPackage, coreSeatCost, additionalSeatCost]);

  const headerCost = useMemo(() => {
    // monthly packages always display the monthly cost, regardless of geo (at this point in time)
    if (isMonthlyPackage) {
      return monthlyFormattedCost;
    }

    // brazil always displays a calculated annual cost
    if (isBrazil) {
      return isTeamsPackage ? annualTeamsFormattedCost : annualFormattedCost;
    }

    // for everything else, monthly
    return monthlyFormattedCost;
  }, [annualTeamsFormattedCost, annualFormattedCost, monthlyFormattedCost, isBrazil, isMonthlyPackage, isTeamsPackage]);

  const headerUnit = useMemo(() => {
    if (isBrazil || isBasic) {
      return '';
    }

    if (isTeamsPackage) {
      return t(COPY.PER_USER_PER_MONTH);
    }

    return t(COPY.PER_MONTH);
  }, [isBasic, isTeamsPackage, isBrazil]);

  const headerByline = useMemo(() => {
    // monthly packages have no byline
    if (isMonthlyPackage) {
      return '';
    }

    // basic is also special
    if (isBasic) {
      return t(COPY.ALWAYS_FREE);
    }

    // Check to see if there's a package specific byline
    const packageCopyKey = `${packageName.toUpperCase()}_BYLINE` as keyof typeof COPY;
    if (packageCopyKey in COPY) {
      return t(COPY[packageCopyKey]);
    }

    // fallback to default text
    const copyKey = `${isTeamsPackage ? 'TEAMS_' : ''}BILLED_ANNUALLY` as keyof typeof COPY;

    // use the COPY file to check to see if there's a country specific byline
    const countryOverrideCopyKey = `${countryCode.toUpperCase()}_${copyKey}` as keyof typeof COPY;
    if (countryOverrideCopyKey in COPY) {
      return t(COPY[countryOverrideCopyKey]);
    }

    return t(COPY[copyKey], { price: annualFormattedCost });
  }, [isBasic, isMonthlyPackage, isTeamsPackage, annualFormattedCost, packageName, countryCode]);

  return {
    isMonthlyPackage,
    isTeamsPackage,
    annualFormattedCost,
    monthlyFormattedCost,
    annualTeamsFormattedCost,
    headerCost,
    headerUnit,
    headerByline,
  };
}
